import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  type Amount,
  type BasePuiDialogProps,
  ButtonWithLoader,
  CurrencyTextField,
  type CustomFieldValidatorState,
  PuiDialog,
  Text,
  useFields,
} from '@pbt/pbt-ui-components'

import { useCalculateDifferenceDistributionMutation } from '~/api/graphql/generated/types'
import { getHighValue } from '~/components/dashboard/invoices/invoiceUtils'

// TODO: Convert to styled when PuiDialog supports it
const useStyles = makeStyles(
  (theme) => ({
    button: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      minWidth: 230,
    },
    input: {
      width: 200,
    },
    dialogTitle: {
      textAlign: 'center',
      borderBottom: 'none',
      paddingTop: theme.spacing(2.5),
    },
  }),
  { name: 'BundleFixedPriceDialog' },
)

interface BundleFixedPriceDialogProps extends BasePuiDialogProps {
  bundleItemHighPrices: number[]
  extendedBundlePrice: Amount
  onOk: (bundlePrice: number, bundleItemDiscounts: number[]) => void
}

/**
 * Sonarqube quality check will fail due to duplicated code from BundleCustomPriceDialog
 * This is intentially built to be functionally identical but visually different
 * Should be removed aas part of CVC-17497
 */
const BundleFixedPriceDialog = ({
  open,
  onClose,
  onOk,
  extendedBundlePrice,
  bundleItemHighPrices,
}: BundleFixedPriceDialogProps) => {
  const classes = useStyles()
  const initialBundleHighPrice = getHighValue(extendedBundlePrice)
  const { t } = useTranslation(['Common', 'Dialogs', 'Validations'])

  const [calculateDifferenceDistribution, { loading, reset }] =
    useCalculateDifferenceDistributionMutation()

  const bundleHighPriceValidator = useCallback(
    ({ state: { bundlePrice } }: CustomFieldValidatorState) =>
      bundlePrice < initialBundleHighPrice,
    [initialBundleHighPrice],
  )

  const {
    fields: { bundlePrice },
    validate,
  } = useFields([
    {
      name: 'bundlePrice',
      label: t('Dialogs:BUNDLE_FIXED_PRICE_DIALOG.TOTAL_BUNDLE_PRICE'),
      validators: [
        {
          validator: bundleHighPriceValidator,
          validatorName: 'bundleHighPrice',
        },
        'greaterThanZero',
      ],
      messages: {
        bundleHighPrice: t(
          'Validations:TOTAL_MUST_BE_LESS_THAN_SUM_INCLUDED_ITEMS',
        ),
        greaterThanZero: t('Validations:TOTAL_MUST_BE_GREATER_THAN_ZERO'),
      },
      initialValue: initialBundleHighPrice,
    },
  ])
  const handleClose = () => {
    reset()
    onClose?.()
  }

  const handleProceed = () => {
    if (validate()) {
      calculateDifferenceDistribution({
        variables: {
          values: bundleItemHighPrices,
          fixedSum: parseFloat(bundlePrice.value),
        },
        onCompleted: (result) => {
          onOk(
            parseFloat(bundlePrice.value),
            result.calculateDifferenceDistribution,
          )
          handleClose()
        },
      })
    }
  }

  const promptText = t('Dialogs:BUNDLE_FIXED_PRICE_DIALOG.PROMPT_TEXT')

  return (
    <PuiDialog
      fullWidth
      aria-labelledby="bundle-fixed-price-dialog"
      classes={{
        dialogTitle: classes.dialogTitle,
      }}
      header={
        <Text variant="h2">{t('Dialogs:BUNDLE_FIXED_PRICE_DIALOG.TITLE')}</Text>
      }
      maxWidth="sm"
      open={open}
      onClose={handleClose}
    >
      <Grid container alignItems="center" direction="column" p={3} pt={0}>
        <Text
          align="center"
          m={2}
          style={{ whiteSpace: 'pre-line' }}
          variant="body"
        >
          {promptText}
        </Text>
        <CurrencyTextField
          TooltipProps={{
            placement: 'top',
          }}
          className={classes.input}
          field={bundlePrice}
          label={bundlePrice.label}
        />
        <Grid container direction="row" justifyContent="center">
          <ButtonWithLoader
            className={classes.button}
            color="secondary"
            variant="outlined"
            onClick={handleClose}
          >
            {t('Common:CANCEL_ACTION')}
          </ButtonWithLoader>
          <ButtonWithLoader
            className={classes.button}
            loading={loading}
            onClick={handleProceed}
          >
            {t('Dialogs:BUNDLE_FIXED_PRICE_DIALOG.SET_TOTAL')}
          </ButtonWithLoader>
        </Grid>
      </Grid>
    </PuiDialog>
  )
}

export default BundleFixedPriceDialog
